/**
 * ClearanceDocAnnotationPage
 * 文件標注頁面 — 報關士在通關文件上框選來源區域（匡列指位），對應報關欄位
 *
 * 佈局：左側 PDF 檢視器 + Canvas 框選工具；右側欄位清單（AnnotationPanel）
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, InputLabel, MenuItem, Paper, Select,
  Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { useMessage } from '../contexts/MessageContext';
import {
  clearanceAnnotationService,
  CLEARANCE_FIELDS,
  type DocAnnotation,
  type AnnotationBBox,
  type ClearanceFieldCode,
} from '../services/clearanceAnnotationService';
import api from '../api/axios';

// PDF.js worker（react-pdf v10 + pdfjs-dist v5）
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const DOC_TYPE_LABEL: Record<string, string> = {
  air_waybill:         '提單',
  commercial_invoice:  '商業發票',
  packing_list:        '裝箱單',
  customs_declaration: '報單掃描（參考）',
};

// 每個 field_code 的顯示顏色
const FIELD_COLORS: Record<string, string> = {
  field_35: '#f59e0b',
  field_36: '#10b981',
  field_38: '#3b82f6',
  field_39: '#8b5cf6',
  field_40: '#ec4899',
  field_41: '#14b8a6',
};
const getFieldColor = (code: string) => FIELD_COLORS[code] ?? '#6b7280';

interface BBoxState {
  startX: number; startY: number;
  endX: number;   endY: number;
  active: boolean;
}

export default function ClearanceDocAnnotationPage() {
  const { sampleId } = useParams<{ sampleId: string }>();
  const sid = Number(sampleId);
  const { showSuccess, showApiError, showError } = useMessage();

  // ── 文件切換 ────────────────────────────────────────────────────────────────
  const [availableDocs, setAvailableDocs] = useState<string[]>([]);
  const [activeDoc, setActiveDoc]         = useState('commercial_invoice');
  const [pdfUrl, setPdfUrl]               = useState('');

  // ── PDF 頁面 ─────────────────────────────────────────────────────────────────
  const [numPages, setNumPages]   = useState(0);
  const [pageNum, setPageNum]     = useState(1);
  const [pdfWidth, setPdfWidth]   = useState(600);
  const pageRef                   = useRef<HTMLDivElement>(null);

  // ── Canvas 框選 ──────────────────────────────────────────────────────────────
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const [bbox, setBbox]           = useState<BBoxState | null>(null);
  const [selecting, setSelecting] = useState(false);

  // ── 標注資料 ─────────────────────────────────────────────────────────────────
  const [annotations, setAnnotations] = useState<DocAnnotation[]>([]);
  const [loading, setLoading]         = useState(false);

  // ── 欄位選擇 Dialog ──────────────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [pendingBbox, setPendingBbox]   = useState<BBoxState | null>(null);
  const [selFieldCode, setSelFieldCode] = useState<string>('field_35');
  const [selSubField, setSelSubField]   = useState<string>('');

  // 載入文件清單 + 標注
  useEffect(() => {
    if (!sid) return;
    setLoading(true);

    Promise.all([
      clearanceAnnotationService.listDocuments(sid),
      clearanceAnnotationService.list(sid),
    ]).then(([docList, anns]) => {
      const docs = docList.available;
      setAvailableDocs(docs);
      if (docs.length > 0) {
        const first = docs.includes('commercial_invoice') ? 'commercial_invoice' : docs[0];
        setActiveDoc(first);
      }
      setAnnotations(anns);
    }).catch(showApiError).finally(() => setLoading(false));
  }, [sid]);

  // 切換文件時 fetch PDF blob（帶 auth header，responseType:'blob'）→ blob URL → react-pdf
  useEffect(() => {
    if (!sid || !activeDoc || availableDocs.length === 0) return;
    setPageNum(1);
    setPdfUrl('');
    let cancelled = false;
    let objectUrl = '';
    const url = clearanceAnnotationService.getDocumentUrl(sid, activeDoc);
    api.get(url, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPdfUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setPdfUrl(''); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sid, activeDoc, availableDocs]);

  // ── Canvas 框選邏輯 ──────────────────────────────────────────────────────────
  const getRelativeCoords = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): { rx: number; ry: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { rx: 0, ry: 0 };
      const rect = canvas.getBoundingClientRect();
      return {
        rx: (e.clientX - rect.left) / rect.width,
        ry: (e.clientY - rect.top)  / rect.height,
      };
    },
    [],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (activeDoc === 'customs_declaration') return; // 報單只供參考，不框選
      const { rx, ry } = getRelativeCoords(e);
      setBbox({ startX: rx, startY: ry, endX: rx, endY: ry, active: true });
      setSelecting(true);
    },
    [activeDoc, getRelativeCoords],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!selecting || !bbox?.active) return;
      const { rx, ry } = getRelativeCoords(e);
      setBbox(prev => prev ? { ...prev, endX: rx, endY: ry } : null);
    },
    [selecting, bbox, getRelativeCoords],
  );

  const onMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!selecting || !bbox) return;
      setSelecting(false);
      const { rx, ry } = getRelativeCoords(e);
      const finalBbox = { ...bbox, endX: rx, endY: ry, active: false };
      const w = Math.abs(finalBbox.endX - finalBbox.startX);
      const h = Math.abs(finalBbox.endY - finalBbox.startY);
      if (w < 0.01 || h < 0.005) { // 太小的框忽略
        setBbox(null);
        return;
      }
      setBbox(finalBbox);
      setPendingBbox(finalBbox);
      setDialogOpen(true);
    },
    [selecting, bbox, getRelativeCoords],
  );

  // 繪製框選框 + 已標注框
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // 已標注框（依顏色分欄位）
    const pageAnns = annotations.filter(a => a.page_number === pageNum && a.doc_type === activeDoc);
    for (const ann of pageAnns) {
      const x1 = ann.bbox_x1 * W;
      const y1 = ann.bbox_y1 * H;
      const x2 = ann.bbox_x2 * W;
      const y2 = ann.bbox_y2 * H;
      const color = getFieldColor(ann.field_code);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      ctx.fillStyle = color + '22';
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      // 右上角標籤
      const label = ann.sub_field ? `${ann.field_code.replace('field_', '')}/${ann.sub_field}` : ann.field_code.replace('field_', '');
      ctx.fillStyle = color;
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(label, x1 + 2, y1 - 3 < 0 ? y1 + 12 : y1 - 3);
    }

    // 當前拖曳框（虛線）
    if (bbox?.active) {
      const x1 = bbox.startX * W;
      const y1 = bbox.startY * H;
      const x2 = bbox.endX * W;
      const y2 = bbox.endY * H;
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      ctx.setLineDash([]);
    }
  }, [annotations, bbox, pageNum, activeDoc]);

  // ── 確認欄位並建立標注 ────────────────────────────────────────────────────────
  const handleConfirmAnnotation = async () => {
    if (!pendingBbox) return;
    const x1 = Math.min(pendingBbox.startX, pendingBbox.endX);
    const y1 = Math.min(pendingBbox.startY, pendingBbox.endY);
    const x2 = Math.max(pendingBbox.startX, pendingBbox.endX);
    const y2 = Math.max(pendingBbox.startY, pendingBbox.endY);

    if (!selFieldCode) { showError('ERR020006', { field: '欄位', detail: '請選擇欄位' }); return; }

    const fieldDef = CLEARANCE_FIELDS.find(f => f.code === selFieldCode);
    const needSub = (fieldDef?.subFields.length ?? 0) > 0;
    if (needSub && !selSubField) { showError('ERR020006', { field: '子欄位', detail: '請選擇子欄位' }); return; }

    try {
      const ann = await clearanceAnnotationService.create(sid, {
        doc_type:       activeDoc,
        page_number:    pageNum,
        bbox:           { x1, y1, x2, y2 },
        extracted_text: `[框選區域 (${x1.toFixed(3)},${y1.toFixed(3)})-(${x2.toFixed(3)},${y2.toFixed(3)})]`,
        field_code:     selFieldCode,
        sub_field:      needSub ? selSubField : undefined,
      });
      setAnnotations(prev => [...prev, ann]);
      showSuccess('SYS020001', { name: '標注' });
    } catch (err) {
      showApiError(err);
    } finally {
      setDialogOpen(false);
      setBbox(null);
      setPendingBbox(null);
    }
  };

  const handleDeleteAnnotation = async (id: number) => {
    try {
      await clearanceAnnotationService.remove(id);
      setAnnotations(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      showApiError(err);
    }
  };

  const handleGeneratePair = async () => {
    try {
      const result = await clearanceAnnotationService.generatePair(sid);
      showSuccess('SYS020001', { name: `訓練對（${result.annotation_count} 筆標注）` });
    } catch (err) {
      showApiError(err);
    }
  };

  // ── 欄位清單：右側 AnnotationPanel ──────────────────────────────────────────
  const getAnnotationsForField = (code: string, subField?: string) =>
    annotations.filter(a =>
      a.field_code === code && (subField ? a.sub_field === subField : !a.sub_field)
    );

  const selectedFieldDef = CLEARANCE_FIELDS.find(f => f.code === selFieldCode);

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 1 }}>
      {/* 標題列 */}
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6">文件標注</Typography>
        <Chip label={`樣本 #${sid}`} size="small" />
        <Typography variant="caption" color="text.secondary">
          框選通關文件中的文字區域，對應報單欄位編號
        </Typography>
      </Box>

      {/* 文件 Tab */}
      <Tabs
        value={availableDocs.includes(activeDoc) ? activeDoc : availableDocs[0] ?? ''}
        onChange={(_, v) => setActiveDoc(v)}
        sx={{ mb: 1, minHeight: 36 }}
      >
        {availableDocs.map(doc => (
          <Tab
            key={doc}
            value={doc}
            label={DOC_TYPE_LABEL[doc] ?? doc}
            sx={{ minHeight: 36, py: 0.5 }}
          />
        ))}
      </Tabs>

      {/* 主內容：PDF 左側 + AnnotationPanel 右側 */}
      <Box sx={{ flex: 1, display: 'flex', gap: 1, overflow: 'hidden' }}>

        {/* ── 左側：PDF 檢視器 ────────────────────────────────────────────────── */}
        <Paper
          elevation={2}
          sx={{ flex: 1, overflow: 'auto', position: 'relative', p: 1 }}
        >
          {availableDocs.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body1" gutterBottom>此樣本尚無原始文件路徑記錄</Typography>
              <Typography variant="caption">
                此樣本建立時尚未支援文件標注功能。<br />
                請從訓練池重新上傳同一批文件，新批次即可使用文件標注。
              </Typography>
            </Box>
          )}
          {availableDocs.length > 0 && activeDoc === 'customs_declaration' && pdfUrl && (
            <Box
              sx={{
                position: 'absolute', top: 8, left: 8, zIndex: 10,
                bgcolor: 'warning.light', px: 1, py: 0.5, borderRadius: 1,
              }}
            >
              <Typography variant="caption">報單掃描 — 只供參考，無法框選</Typography>
            </Box>
          )}

          <Box sx={{ position: 'relative', display: 'inline-block' }} ref={pageRef}>
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<CircularProgress />}
              error={<Typography color="error">PDF 載入失敗</Typography>}
            >
              <Page
                pageNumber={pageNum}
                width={pdfWidth}
                renderTextLayer={true}
                renderAnnotationLayer={false}
              />
            </Document>

            {/* Canvas overlay */}
            <canvas
              ref={canvasRef}
              width={pdfWidth}
              height={pageRef.current?.clientHeight ?? 800}
              style={{
                position: 'absolute',
                top: 0, left: 0,
                cursor: activeDoc === 'customs_declaration' ? 'default' : 'crosshair',
                pointerEvents: activeDoc === 'customs_declaration' ? 'none' : 'all',
              }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
            />
          </Box>

          {/* 頁碼導覽 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: 'center' }}>
            <Button
              size="small" startIcon={<ArrowBackIcon />}
              disabled={pageNum <= 1}
              onClick={() => setPageNum(p => p - 1)}
            >
              上頁
            </Button>
            <Typography variant="body2">{pageNum} / {numPages || '?'}</Typography>
            <Button
              size="small" endIcon={<ArrowForwardIcon />}
              disabled={pageNum >= numPages}
              onClick={() => setPageNum(p => p + 1)}
            >
              下頁
            </Button>
          </Box>
        </Paper>

        {/* ── 右側：AnnotationPanel ────────────────────────────────────────────── */}
        <Paper
          elevation={2}
          sx={{ width: 320, overflow: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <Typography variant="subtitle2">欄位目標值（來自報單掃描）</Typography>
          <Divider />

          {CLEARANCE_FIELDS.map(field => {
            const hasSubFields = field.subFields.length > 0;
            const fieldAnns = annotations.filter(a => a.field_code === field.code);

            return (
              <Box key={field.code} sx={{ borderLeft: `3px solid ${getFieldColor(field.code)}`, pl: 1, mb: 0.5 }}>
                <Typography variant="caption" fontWeight="bold" color={getFieldColor(field.code)}>
                  {field.name}
                </Typography>

                {hasSubFields ? (
                  field.subFields.map(sub => {
                    const subAnns = fieldAnns.filter(a => a.sub_field === sub);
                    return (
                      <Box key={sub} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        {subAnns.length > 0
                          ? <CheckCircleIcon fontSize="inherit" color="success" />
                          : <RadioButtonUncheckedIcon fontSize="inherit" color="disabled" />
                        }
                        <Typography variant="caption">{sub}</Typography>
                        {subAnns.length > 0 && (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              {subAnns[0].extracted_text.substring(0, 30)}
                            </Typography>
                            <Tooltip title="刪除標注">
                              <DeleteIcon
                                fontSize="inherit"
                                sx={{ cursor: 'pointer', color: 'error.light', ml: 'auto' }}
                                onClick={() => handleDeleteAnnotation(subAnns[0].id)}
                              />
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    {fieldAnns.length > 0
                      ? <CheckCircleIcon fontSize="inherit" color="success" />
                      : <RadioButtonUncheckedIcon fontSize="inherit" color="disabled" />
                    }
                    {fieldAnns.length > 0 ? (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          {fieldAnns[0].extracted_text.substring(0, 40)}
                        </Typography>
                        <Tooltip title="刪除標注">
                          <DeleteIcon
                            fontSize="inherit"
                            sx={{ cursor: 'pointer', color: 'error.light', ml: 'auto' }}
                            onClick={() => handleDeleteAnnotation(fieldAnns[0].id)}
                          />
                        </Tooltip>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.disabled">未框選</Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}

          <Divider sx={{ mt: 1 }} />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleGeneratePair}
            disabled={annotations.length === 0}
          >
            產生訓練對（{annotations.length} 筆標注）
          </Button>
        </Paper>
      </Box>

      {/* ── 欄位選擇 Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setBbox(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>選擇欄位對應</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>報單欄位</InputLabel>
            <Select
              value={selFieldCode}
              label="報單欄位"
              onChange={e => { setSelFieldCode(e.target.value); setSelSubField(''); }}
            >
              {CLEARANCE_FIELDS.map(f => (
                <MenuItem key={f.code} value={f.code}>{f.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {(selectedFieldDef?.subFields.length ?? 0) > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>子欄位</InputLabel>
              <Select
                value={selSubField}
                label="子欄位"
                onChange={e => setSelSubField(e.target.value)}
              >
                <MenuItem value=""><em>（請選擇）</em></MenuItem>
                {selectedFieldDef?.subFields.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setBbox(null); }}>取消</Button>
          <Button variant="contained" onClick={handleConfirmAnnotation}>確認</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
